import classNames from 'classnames'
import { addDays, addMonths, addWeeks, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek, subDays, subMonths, subWeeks } from 'date-fns'
import { uk } from 'date-fns/locale'
import React, { useEffect, useMemo, useState } from 'react'

import { useActions } from '@hooks/useActions'
import { useQueryParams } from '@hooks/useQueryParams'
import { useWindowSize } from '@hooks/useWindowSize'

import { formatDateInGenitive } from '@assets/utils'

import type { ScheduleStepperProps } from './ScheduleStepper.props'

export function ScheduleStepper({ title = 'Розклад' }: ScheduleStepperProps) {
  const { getQuery, setQuery } = useQueryParams()
  const isDay = getQuery('range') === 'day'
  const isWeek = getQuery('range') === 'week'
  const isMonth = getQuery('range') === 'month'
  const start = getQuery('start')

  const { setShowCalendarOnMobile } = useActions()

  const { width } = useWindowSize()

  const [currentDate, setCurrentDate] = useState(start ? new Date(start) : new Date())

  const handlePrev = () => {
    setCurrentDate((currDate) => {
      if (isDay) return subDays(currDate, 1)
      if (isWeek) return subWeeks(currDate, 1)

      return subMonths(currDate, 1)
    })
  }

  const handleNext = () => {
    setCurrentDate((currDate) => {
      if (isDay) return addDays(currDate, 1)
      if (isWeek) return addWeeks(currDate, 1)

      return addMonths(currDate, 1)
    })
  }

  const handleShowCalendarOnMobile = () => {
    width < 992 && setShowCalendarOnMobile()
  }

  const formatRange = useMemo(() => {
    if (isDay) {
      return formatDateInGenitive(currentDate)
    }

    if (isWeek) {
      const startOfWeekDate = startOfWeek(currentDate, { weekStartsOn: 1 })
      const endOfWeekDate = endOfWeek(currentDate, { weekStartsOn: 1 })

      const startOfWeekFormatted = formatDateInGenitive(startOfWeekDate)
      const endOfWeekFormatted = formatDateInGenitive(endOfWeekDate)

      return `${startOfWeekFormatted.split(' ')[0]} - ${width < 992 ? endOfWeekFormatted.split(' ').slice(0, -1).join(' ') : endOfWeekFormatted}`
    }

    return format(currentDate, 'LLLL yyyy', { locale: uk })
  }, [currentDate, isDay, isWeek])

  useEffect(() => {
    if (isDay) {
      setQuery('start', format(currentDate, 'yyyy-MM-dd'))
      setQuery('end', format(addDays(currentDate, 1), 'yyyy-MM-dd'))
    }

    if (isWeek) {
      setQuery('start', format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'))
      setQuery('end', format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'))
    }

    if (isMonth) {
      setQuery('start', format(startOfMonth(currentDate), 'yyyy-MM-dd'))
      setQuery('end', format(endOfMonth(currentDate), 'yyyy-MM-dd'))
    }
  }, [currentDate, isDay, isWeek, isMonth])

  useEffect(() => {
    isDay && start && setCurrentDate(new Date(start))
  }, [start])

  return (
    <>
      <h2 className="header__title">{title}</h2>
      <div
        className={classNames('header__stepper stepper', { '--isDay': isDay })}
        onClick={isDay ? handleShowCalendarOnMobile : undefined}
      >
        <button
          className="stepper__button"
          onClick={handlePrev}
        >
          <svg className="stepper__button-svg">
            <use href="/img/sprite.svg#chevron-left"></use>
          </svg>
        </button>
        <svg className="stepper__icon">
          <use href="/img/sprite.svg#calendar"></use>
        </svg>
        <span className={classNames('stepper__value', { month: isMonth })}>{formatRange}</span>
        <button
          className="stepper__button"
          onClick={handleNext}
        >
          <svg className="stepper__button-svg">
            <use href="/img/sprite.svg#chevron-right"></use>
          </svg>
        </button>
      </div>
    </>
  )
}
